import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Trophy, Play, CheckCircle, Clock } from 'lucide-react';
import Button from '../UI/Button';

const BracketContainer = styled.div`
  padding: 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 12px;
  border: 1px solid #2d2d44;
`;

const BracketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #2d2d44;
`;

const TournamentTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TournamentStatus = styled.div`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'active': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'completed': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const RoundsContainer = styled.div`
  display: flex;
  gap: 30px;
  overflow-x: auto;
  padding: 20px 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Round = styled.div`
  min-width: 300px;
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
`;

const RoundTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
  padding-bottom: 10px;
  border-bottom: 2px solid #00d4ff;
`;

const Match = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const MatchNumber = styled.div`
  color: #6c6c8a;
  font-size: 12px;
  font-weight: 600;
`;

const MatchStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => {
    switch(props.status) {
      case 'finished': return '#4ecdc4';
      case 'active': return '#ffd93d';
      case 'pending': return '#6c6c8a';
      default: return '#6c6c8a';
    }
  }};
`;

const TeamsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Team = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: ${props => props.winner ? 'linear-gradient(90deg, rgba(78, 205, 196, 0.2) 0%, transparent 100%)' : 'transparent'};
  border: ${props => props.winner ? '1px solid #4ecdc4' : '1px solid #2d2d44'};
  border-radius: 6px;
  transition: all 0.3s ease;
`;

const TeamName = styled.div`
  color: ${props => props.winner ? '#4ecdc4' : '#ffffff'};
  font-weight: ${props => props.winner ? '600' : '500'};
  font-size: 14px;
`;

const TeamScore = styled.div`
  color: ${props => props.winner ? '#4ecdc4' : '#6c6c8a'};
  font-weight: 600;
  font-size: 14px;
`;

const MatchActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const TournamentBracket = ({ 
  bracketData, 
  isLoading, 
  onRecordResult, 
  onStartNextRound 
}) => {
  if (isLoading) {
    return (
      <BracketContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری براکت...</div>
        </div>
      </BracketContainer>
    );
  }

  if (!bracketData || !bracketData.tournamentMatches || bracketData.tournamentMatches.length === 0) {
    return (
      <BracketContainer>
        <EmptyState>
          <EmptyIcon>🏆</EmptyIcon>
          <EmptyTitle>براکت تورنومنت</EmptyTitle>
          <EmptyDescription>
            هنوز مسابقه‌ای برای این تورنومنت ایجاد نشده است
          </EmptyDescription>
        </EmptyState>
      </BracketContainer>
    );
  }

  // Group matches by round
  const matchesByRound = bracketData.tournamentMatches.reduce((acc, match) => {
    (acc[match.round] = acc[match.round] || []).push(match);
    return acc;
  }, {});

  const getMatchStatus = (match) => {
    if (match.status === 'Finished') return 'finished';
    if (match.teamOneName && match.teamTwoName && match.teamOneName !== 'BYE' && match.teamTwoName !== 'BYE') {
      return 'active';
    }
    return 'pending';
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'finished': return 'تکمیل شده';
      case 'active': return 'در حال انجام';
      case 'pending': return 'در انتظار';
      default: return 'نامشخص';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'finished': return <CheckCircle size={14} />;
      case 'active': return <Play size={14} />;
      case 'pending': return <Clock size={14} />;
      default: return <Clock size={14} />;
    }
  };

  // Check if current round is finished (like old website)
  const currentRoundMatches = matchesByRound[bracketData?.currentRound] || [];
  const isRoundFinished = currentRoundMatches.length > 0 && currentRoundMatches.every(match => match.status === 'Finished');
  const isTournamentFinished = bracketData?.status === 'Finished';

  return (
    <BracketContainer>
      <BracketHeader>
        <TournamentTitle>
          <Trophy size={24} />
          {bracketData.name || 'تورنومنت'}
        </TournamentTitle>
        <TournamentStatus status={bracketData.status}>
          {bracketData.status === 'active' ? 'در حال انجام' : 
           bracketData.status === 'completed' ? 'تکمیل شده' : 'در انتظار'}
        </TournamentStatus>
      </BracketHeader>

      <RoundsContainer>
        {Object.keys(matchesByRound)
          .map(Number)
          .sort((a, b) => a - b)
          .map(roundNumber => (
            <Round key={roundNumber}>
              <RoundTitle>
                دور {roundNumber}
              </RoundTitle>
              
              {matchesByRound[roundNumber].map(match => {
                const matchStatus = getMatchStatus(match);
                return (
                  <Match
                    key={match.matchId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MatchHeader>
                      <MatchNumber>مسابقه {match.matchId}</MatchNumber>
                      <MatchStatus status={matchStatus}>
                        {getStatusIcon(matchStatus)}
                        {getStatusText(matchStatus)}
                      </MatchStatus>
                    </MatchHeader>

                    <TeamsContainer>
                      <Team winner={match.winnerName === match.teamOneName}>
                        <TeamName>{match.teamOneName || 'TBD'}</TeamName>
                        <TeamScore>{match.teamOneScore || 0}</TeamScore>
                      </Team>
                      
                      <div style={{ 
                        textAlign: 'center', 
                        color: '#6c6c8a', 
                        fontSize: '12px',
                        fontWeight: 'bold',
                        margin: '5px 0'
                      }}>
                        VS
                      </div>
                      
                      <Team winner={match.winnerName === match.teamTwoName}>
                        <TeamName>{match.teamTwoName || 'TBD'}</TeamName>
                        <TeamScore>{match.teamTwoScore || 0}</TeamScore>
                      </Team>
                    </TeamsContainer>

                    {matchStatus === 'active' && (
                      <MatchActions>
                        <ActionButton onClick={() => onRecordResult(match)}>
                          ثبت نتیجه
                        </ActionButton>
                      </MatchActions>
                    )}
                  </Match>
                );
              })}
            </Round>
          ))}
      </RoundsContainer>

      {isRoundFinished && !isTournamentFinished && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px',
          padding: '20px',
          background: 'linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          borderRadius: '12px'
        }}>
          <Button
            variant="primary"
            onClick={() => onStartNextRound(bracketData.id)}
            icon={<Play size={16} />}
          >
            شروع دور {bracketData.currentRound + 1}
          </Button>
        </div>
      )}
    </BracketContainer>
  );
};

export default TournamentBracket;
